import { sanitizeMatchType, sanitizeRegion } from './inputSanitizer';

export const MATCH_TYPE_CATEGORIES = [
  { value: 'OFFICIAL', label: 'Official Matches' },
  { value: 'PRACTICE', label: 'Practice / Scrims' },
];

export const MATCH_TYPE_REGIONS = [
  'GLOBAL',
  'NORTH AMERICA',
  'LATIN AMERICA',
  'EUROPE',
  'MIDDLE EAST',
  'INDIA',
  'JAPAN',
  'CHINA',
  'GARENA',
  'AFRICA',
];

export const slugifyMatchType = (parts) =>
  parts
    .filter(Boolean)
    .join(' ')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

export const inferMatchTypeCategory = (label = '') => {
  const value = String(label).toUpperCase();
  return value.includes('SCRIM') || value.includes('PRACTICE') ? 'PRACTICE' : 'OFFICIAL';
};

export const normalizeMatchTypeCatalog = (rawTypes = [], season = '') => {
  if (!Array.isArray(rawTypes)) return [];

  return rawTypes
    .map((item, index) => {
      const legacyLabel = typeof item === 'string' ? item : item?.label || item?.name || item?.MatchType || '';
      const label = sanitizeMatchType(legacyLabel);
      if (!label) return null;

      const rawCategory = typeof item === 'object' ? item.category || item.MatchCategory : '';
      const category = MATCH_TYPE_CATEGORIES.some(option => option.value === String(rawCategory).toUpperCase())
        ? String(rawCategory).toUpperCase()
        : inferMatchTypeCategory(label);

      const rawRegion = typeof item === 'object' ? item.region || item.MatchTypeRegion : '';
      const region = sanitizeRegion(rawRegion) || 'GLOBAL';
      const id = typeof item === 'object' && item.id
        ? String(item.id)
        : slugifyMatchType([season, category, region, label, index]);

      return {
        id,
        label,
        category,
        region,
        season: String(typeof item === 'object' ? item.season || season : season),
        active: typeof item === 'object' && item.active === false ? false : true,
      };
    })
    .filter(Boolean);
};

export const catalogToLegacyMatchTypes = (catalog = [], { includeInactive = false } = {}) => (
  [...new Set(
    catalog
      .filter(item => includeInactive || item.active !== false)
      .map(item => item.label)
      .filter(Boolean)
  )]
);

export const getMatchTypeRegions = (catalog = [], category = '') => {
  const categoryKey = String(category || '').toUpperCase();
  return [...new Set(
    catalog
      .filter(item => item.active !== false)
      .filter(item => !categoryKey || item.category === categoryKey)
      .map(item => item.region || 'GLOBAL')
      .filter(Boolean)
  )].sort();
};

export const getMatchTypeOptions = (catalog = [], { category = '', region = '' } = {}) => {
  const categoryKey = String(category || '').toUpperCase();
  const regionKey = String(region || '').toUpperCase();
  return catalog
    .filter(item => item.active !== false)
    .filter(item => !categoryKey || item.category === categoryKey)
    .filter(item => !regionKey || String(item.region || '').toUpperCase() === regionKey)
    .sort((a, b) => a.label.localeCompare(b.label));
};

export const findMatchTypeEntry = (catalog = [], { label = '', category = '', region = '' } = {}) => {
  const labelKey = sanitizeMatchType(label);
  const categoryKey = String(category || '').toUpperCase();
  const regionKey = String(region || '').toUpperCase();
  if (!labelKey) return null;

  return catalog.find(item =>
    item.label === labelKey &&
    (!categoryKey || item.category === categoryKey) &&
    (!regionKey || String(item.region || '').toUpperCase() === regionKey)
  ) || catalog.find(item => item.label === labelKey) || null;
};

export const getSeasonConfig = (config, season) => {
  if (!config) return { vsTeams: [], matchTypes: [], matchTypeCatalog: [] };

  let seasonConfig = null;
  if (config.seasons && config.seasons[season]) {
    seasonConfig = config.seasons[season];
  } else if (String(season) === '2025' && (config.vsTeams || config.matchTypes || config.matchTypeCatalog)) {
    seasonConfig = {
      vsTeams: config.vsTeams || [],
      matchTypes: config.matchTypes || [],
      matchTypeCatalog: config.matchTypeCatalog || [],
    };
  }

  if (!seasonConfig) return { vsTeams: [], matchTypes: [], matchTypeCatalog: [] };

  const catalog = normalizeMatchTypeCatalog(
    seasonConfig.matchTypeCatalog || seasonConfig.matchTypes || [],
    season
  );

  return {
    ...seasonConfig,
    vsTeams: seasonConfig.vsTeams || [],
    matchTypeCatalog: catalog,
    matchTypes: catalogToLegacyMatchTypes(catalog),
  };
};
